import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, ChevronRight, FileText, PenLine, SlidersHorizontal, ExternalLink, Zap, BookOpen, Camera, CalendarDays, Store, GitCompare, BarChart3, Map } from "lucide-react";
import { Shell } from "@/components/Shell";
import { CASALE } from "@/data/questionnaire";
import { getType, belongsToCurrent } from "@/data/business";
import { loadAll, removeSubmission, formatDate, createSubmission } from "@/lib/storage";

const Row = ({ s, onOpen, onDelete }) => (
  <div className="card p-5 sm:p-6 flex items-center gap-4 fade-up" data-testid={`submission-row-${s.id}`}>
    <div className={`flex-none w-12 h-12 rounded-2xl grid place-items-center ${s.status === "completed" ? "bg-[#2D4030] text-[#FAF6F0]" : "bg-[#EAE0D0] text-[#8C6D58]"}`}>
      {s.status === "completed" ? <FileText size={22} /> : <PenLine size={22} />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-serif text-xl font-semibold text-[#2C221E] truncate" data-testid="submission-respondent">{s.respondent || "Senza nome"}</p>
      <p className="text-sm text-[#7A6B63]">
        {formatDate(s.createdAt)} · {s.status === "completed" ? "Completato" : s.mode === "live" ? "Compilazione rapida in corso" : "Bozza in corso"}{s.businessName ? ` · ${s.businessName}` : ""}{s.notes?.trim() ? " · con note" : ""}{s.staff?.length ? ` · ${s.staff.length} staff` : ""}
      </p>
    </div>
    <button className="btn btn-ghost !min-h-[48px] !px-4" onClick={onDelete} aria-label="Elimina" data-testid="delete-submission-btn">
      <Trash2 size={18} />
    </button>
    <button className="btn btn-olive !min-h-[48px]" onClick={onOpen} data-testid="open-submission-btn">
      {s.status === "completed" ? "Apri risultato" : "Riprendi"} <ChevronRight size={18} />
    </button>
  </div>
);

export default function HomePage() {
  const navigate = useNavigate();
  const [list, setList] = useState(() => loadAll().filter(belongsToCurrent));

  const del = (id) => {
    if (!window.confirm("Eliminare questa compilazione dal tablet?")) return;
    removeSubmission(id);
    setList(loadAll().filter(belongsToCurrent));
  };

  return (
    <Shell header={null}>
      <div className="fade-up">
        <p className="eyebrow">Strumento del consulente · {getType(CASALE.type).label}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2C221E] mt-2">{CASALE.name}</h1>
        <p className="text-base sm:text-lg text-[#5C4D46] mt-3 max-w-xl">{CASALE.tagline}. Un breve questionario da far compilare ai titolari sul tablet, per capire cosa vogliono davvero.</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
          <button className="text-sm text-[#A64B2A] underline underline-offset-4 min-h-[40px]" onClick={() => navigate("/attivita")} data-testid="business-link-btn">Cambia attività, nome o tipologia</button>
          <button className="text-sm text-[#A64B2A] underline underline-offset-4 min-h-[40px]" onClick={() => navigate("/impostazioni")} data-testid="settings-link-btn">Cambia PIN</button>
        </div>
        <div className="flex flex-wrap gap-3 mt-8 items-center">
          <button className="btn btn-primary text-lg !min-h-[64px] !px-9" onClick={() => navigate("/nuovo")} data-testid="new-questionnaire-btn">
            <Plus size={22} /> Nuovo questionario
          </button>
          <button className="btn btn-olive text-lg !min-h-[64px] !px-7" onClick={() => navigate(`/rapido/${createSubmission("", "live").id}`)} data-testid="live-mode-btn">
            <Zap size={20} /> Compilazione rapida
          </button>
          <button className="btn btn-ghost" onClick={() => navigate("/attivita")} data-testid="business-link-btn-2">
            <Store size={18} /> Attività
          </button>
          <button className="btn btn-ghost" onClick={() => navigate("/foto")} data-testid="photo-link-btn">
            <Camera size={18} /> Compila da foto
          </button>
          <button className="btn btn-ghost" onClick={() => navigate("/eventi")} data-testid="events-link-btn">
            <CalendarDays size={18} /> Schede evento
          </button>
          <button className="btn btn-ghost" onClick={() => navigate("/metodo")} data-testid="method-link-btn">
            <BookOpen size={18} /> Metodo operativo
          </button>
          <button className="btn btn-ghost" onClick={() => window.open("/modulo", "_blank", "noopener")} data-testid="blank-form-link-btn">
            <FileText size={18} /> Modulo da stampare <ExternalLink size={14} className="opacity-60" />
          </button>
          <button className="btn btn-ghost" onClick={() => navigate("/pesi")} data-testid="weights-link-btn">
            <SlidersHorizontal size={18} /> Pesi delle azioni
          </button>
          <button className="btn btn-ghost" onClick={() => navigate("/confronto")} data-testid="compare-link-btn">
            <GitCompare size={18} /> Confronta attività
          </button>
          <button className="btn btn-ghost" onClick={() => navigate("/benchmark")} data-testid="benchmark-link-btn">
            <BarChart3 size={18} /> Benchmark di settore
          </button>
          <button className="btn btn-ghost" onClick={() => navigate("/territorio")} data-testid="territory-link-btn">
            <Map size={18} /> Ricerca del territorio
          </button>
        </div>
      </div>

      <section className="mt-14">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#382E2B]">Compilazioni su questo tablet</h2>
          <span className="text-sm text-[#7A6B63]" data-testid="submissions-count">{list.length}</span>
        </div>
        {list.length === 0 ? (
          <div className="card p-8 text-center text-[#7A6B63]" data-testid="empty-archive">Nessuna compilazione ancora. Inizia dal pulsante qui sopra.</div>
        ) : (
          <div className="grid gap-4" data-testid="submissions-list">
            {list.map((s) => (
              <Row key={s.id} s={s} onDelete={() => del(s.id)} onOpen={() => navigate(s.status === "completed" ? `/risultato/${s.id}` : s.mode === "live" ? `/rapido/${s.id}` : `/compila/${s.id}`)} />
            ))}
          </div>
        )}
      </section>
    </Shell>
  );
}
